import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, IconButton, Slide } from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

/**
 * Toast notifications empilhadas no canto inferior direito.
 * Exibe título + preview (summary/message), sem body HTML.
 * Clique navega para actionUrl. Auto-dismiss 20s (pausável no hover).
 */
export default function NotificationToast({
  toasts,
  onDismiss,
  onPauseTimer,
  onResumeTimer,
}) {
  const navigate = useNavigate();

  const handleClick = (toast) => {
    const url = toast.actionUrl || toast.data?.actionUrl;
    if (!url) return;

    onDismiss(toast._toastId);
    if (url.startsWith('/')) {
      navigate(url);
    } else {
      window.open(url, '_blank');
    }
  };

  if (toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: '5%',
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 360 },
        width: { xs: 'calc(100vw - 32px)', sm: 360 },
      }}
    >
      {toasts.map((toast) => {
        const preview = toast.summary || toast.message || '';
        const hasAction = !!(toast.actionUrl || toast.data?.actionUrl);

        return (
          <Slide key={toast._toastId} direction="left" in mountOnEnter unmountOnExit>
            <Paper
              elevation={6}
              onMouseEnter={() => onPauseTimer(toast._toastId)}
              onMouseLeave={() => onResumeTimer(toast._toastId)}
              onClick={() => handleClick(toast)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 2,
                pr: 5,
                borderRadius: 2,
                borderLeft: 4,
                borderColor: 'primary.main',
                cursor: hasAction ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  boxShadow: 10,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <NotificationsIcon
                sx={{ color: 'primary.main', mt: 0.25, fontSize: 22, flexShrink: 0 }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  noWrap
                  sx={{ lineHeight: 1.3 }}
                >
                  {toast.title || 'Notificação'}
                </Typography>

                {preview && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {preview}
                  </Typography>
                )}
              </Box>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(toast._toastId);
                }}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  opacity: 0.6,
                  '&:hover': { opacity: 1 },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Slide>
        );
      })}
    </Box>
  );
}
